import React, { forwardRef } from 'react';
import styled from '@emotion/styled';
import { useSelector } from 'react-redux';
import useEnroll from '@hooks/useEnroll';
import Dialog from '@comp/Dialog/Dialog';
import { getEnrolledCoursesFeed } from '@selectors/syllabus';
import { evtNameCategoryMap, staticStrings } from '@constants/index';
import { Spacings, Fonts, mediaQuery } from '@styles/index';
import PlusCard from '@cont/PlusCourses/PlusCard';
import { enrollProps } from '@cont/PlusCourses/syllabusHelper';
import H4 from '@base/H4/H4';
import Grow from '@base/Grow/Grow';
import Link from '@base/Link/Link';
import P1 from '@base/P1/P1';
import { useRouter } from 'next/router';
import { segment } from '@actions/analytics';
import {
  SYLLABUS_SEE_ALL_CLICKED,
  TOPIC_SYLLABUS_PAGE
} from '@constants/segment';
import useGoalEventProps from '@hooks/useGoalEventProps';
import useIsRecordedGoal from '@hooks/useIsRecordedGoal';

interface ITopic {
  uid: string;
}

interface IEnrolledCoursesProps {
  topic: ITopic;
}

const Wrapper = styled.div`
  margin-top: ${Spacings.SPACING_20B};
`;

const BlockWrapper = styled.div`
  grid-column: span 24;
  display: grid;
  grid-template-columns: repeat(24, 1fr);
  grid-gap: ${Spacings.SPACING_4B};
  ${mediaQuery.uptoMobile} {
    grid-template-columns: repeat(1, 1fr);
  }
`;

const BoldP1 = styled(P1)`
  font-weight: ${Fonts.BOLD};
  line-height: 14px;
`;

const LabelDiv = styled.div`
  grid-column: span 18;
  display: flex;
  margin-bottom: ${Spacings.SPACING_8B};
  align-items: center;
`;

const SeeAllLink = styled.div`
  ${mediaQuery.uptoMobile} {
    display: none;
  }
`;

const imageBorderRadius = `${Spacings.SPACING_2B} ${Spacings.SPACING_0B} ${Spacings.SPACING_0B} ${Spacings.SPACING_2B}`;

const EnrolledCourses = forwardRef<HTMLDivElement, IEnrolledCoursesProps>(
  ({ topic }, ref) => {
    const feedBlock = useSelector((state) =>
      getEnrolledCoursesFeed(state, topic.uid)
    );
    const { query } = useRouter();
    const { goalSlug, goalUID, topicUid: topicGroupUID } = query;
    const isRecordedGoal = useIsRecordedGoal(goalUID);
    const { isUnEnrollModalOpen, toggleEnroll, acceptOnClick, onCloseModal } =
      useEnroll({ ...enrollProps, isRecordedGoal });
    const goalSegmentData = useGoalEventProps({ goalUID: goalUID as string });

    if (!feedBlock.results.length) return null;

    const renderBlock = () => {
      return (
        <>
          {feedBlock.results.slice(0, 4).map((courseData, index) => (
            <PlusCard
              key={courseData.course}
              courseData={courseData}
              toggleEnroll={toggleEnroll}
              index={index}
              imageBorderRadius={imageBorderRadius}
              showProgress
              orientation="vertical"
              gridColumn="span 6"
            />
          ))}
        </>
      );
    };

    const seeAllClick = () => {
      segment.track(
        SYLLABUS_SEE_ALL_CLICKED,
        {
          ...goalSegmentData,
          last_primary_source: TOPIC_SYLLABUS_PAGE,
          last_primary_source_section: 'Topic Page - Enrolments'
        },
        evtNameCategoryMap.WITHOUT_CATEGORY,
        true
      );
    };

    return (
      <Wrapper ref={ref}>
        <LabelDiv>
          <H4>Enrollments</H4>
          <Grow />
          {feedBlock.results.length > 4 && (
            <SeeAllLink>
              <Link
                href={{
                  pathname: `/goal/${goalSlug}/${goalUID}/enrolled`,
                  query: { topicGroupUID }
                }}
                onClick={seeAllClick}
              >
                <BoldP1 color="var(--color-i-green)">SEE ALL</BoldP1>
              </Link>
            </SeeAllLink>
          )}
        </LabelDiv>
        <BlockWrapper>{renderBlock()}</BlockWrapper>
        <Dialog
          onClose={onCloseModal}
          dialogState={isUnEnrollModalOpen}
          heading="Are you sure you want to unenroll?"
          message={staticStrings.UNENROLL_MESSAGE}
          acceptLabel="Unenroll"
          rejectLabel="Cancel"
          acceptOnClick={acceptOnClick}
          rejectOnClick={onCloseModal}
        />
      </Wrapper>
    );
  }
);

EnrolledCourses.displayName = 'EnrolledCourses';

export default EnrolledCourses;
